import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Utility functions for animations and interactions
 */

/**
 * Linear interpolation with optional exponential damping
 */
export function lerpVal(current: number, target: number, speed: number, delta: number, exp: number = 1): number {
  const t = 1 - Math.pow(1 - speed * delta, exp);
  return current + (target - current) * t;
}

/**
 * Damp a 3D vector towards a target with spring-like motion
 */
export function damp3Val(current: any, target: number[], lambda: number, delta: number) {
  const t = 1 - Math.exp(-lambda * delta);
  if (Array.isArray(current)) {
    // Handle array
    current[0] += (target[0] - current[0]) * t;
    current[1] += (target[1] - current[1]) * t;
    current[2] += (target[2] - current[2]) * t;
  } else {
    // Handle Vector3-like object
    current.x += (target[0] - current.x) * t;
    current.y += (target[1] - current.y) * t;
    current.z += (target[2] - current.z) * t;
  }
}

/**
 * Transform hero progress (0-1) to a color string (white to black)
 * with cubic easing near the end (0.9-1.0).
 * Used by FloatingBrand and RailStack.
 */
export function heroColorTransform(v: number) {
  const value = typeof v === "number" ? v : 0;
  // Start transition at 0.9, end at 1.0
  const t = Math.max(0, Math.min(1, (value - 0.9) / 0.1));
  // Cubic easing in-out
  const cubicT = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

  // Interpolate between white (255 255 255) and black (35 31 32)
  const r = Math.round(255 * (1 - cubicT) + 35 * cubicT);
  const g = Math.round(255 * (1 - cubicT) + 31 * cubicT);
  const b = Math.round(255 * (1 - cubicT) + 32 * cubicT);
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Transform hero progress (0-1) to a color string (black to white)
 * with cubic easing near the end (0.9-1.0).
 * Inverse of heroColorTransform.
 */
export function heroInverseColorTransform(v: number) {
  const value = typeof v === "number" ? v : 0;
  // Start transition at 0.9, end at 1.0
  const t = Math.max(0, Math.min(1, (value - 0.9) / 0.1));
  // Cubic easing in-out
  const cubicT = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  
  // Interpolate between black (0) and white (255)
  const r = Math.round(255 * (1 - cubicT) + 35 * cubicT);
  const g = Math.round(255 * (1 - cubicT) + 31 * cubicT);
  const b = Math.round(255 * (1 - cubicT) + 32 * cubicT);
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Detect browser type
 */
export const detectBrowser = (): string => {
  if (typeof window === 'undefined') return 'SSR';
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) return 'Chromium';
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
  if (userAgent.includes('Edg')) return 'Edge';
  if (userAgent.includes('Opera') || !userAgent.includes('OPR')) return 'Opera';
  return 'Unknown';
};

/**
 * Copy text to clipboard (fallback method)
 */
export const copyToClipboard = (text: string) => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'absolute';
  textArea.style.left = '-9999px';
  document.body.appendChild(textArea);
  textArea.select();
  try {
    document.execCommand('copy');
  } catch (err) {
    console.error('Fallback: Oops, unable to copy', err);
  }
  document.body.removeChild(textArea);
};
